import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Icon,
  CloseButton,
  VStack,
  HStack,
  Image,
  useToast,
} from '@chakra-ui/react';
import { FaDownload, FaMobileAlt } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const toast = useToast();

  useEffect(() => {
    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed === 'true') {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired');
      // Prevent the default mini-infobar from appearing
      e.preventDefault();
      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      // Show our custom install prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('App is already installed');
      setShowPrompt(false);
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      console.log('No deferred prompt available');
      return;
    }

    // Show the browser's install prompt
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    if (outcome === 'accepted') {
      toast({
        title: 'App Installing',
        description: 'Lake Lucien Surgery Center is being added to your home screen!',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  const handleDontShowAgain = () => {
    localStorage.setItem('pwa-install-dismissed', 'true');
    setShowPrompt(false);
    toast({
      title: 'Got it!',
      description: "We won't show this message again.",
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <>
      {/* Desktop/Tablet Prompt - Top Banner */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex={9999}
        bg="linear-gradient(135deg, #2196f3 0%, #00bcd4 100%)"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
        py={4}
        px={6}
      >
        <Flex
          maxW="container.xl"
          mx="auto"
          align="center"
          justify="space-between"
          gap={4}
        >
          <HStack spacing={4} flex={1}>
            <Icon
              as={FaDownload}
              boxSize={8}
              color="white"
              display={{ base: 'none', lg: 'block' }}
            />
            <VStack align="start" spacing={1}>
              <Text
                color="white"
                fontSize={{ base: 'md', lg: 'lg' }}
                fontWeight="700"
                letterSpacing="wide"
              >
                Install Lake Lucien Surgery Center App
              </Text>
              <Text
                color="whiteAlpha.900"
                fontSize={{ base: 'xs', lg: 'sm' }}
                fontWeight="500"
              >
                Get quick access from your desktop or home screen • Works offline • Faster loading
              </Text>
            </VStack>
          </HStack>

          <HStack spacing={3}>
            <Button
              onClick={handleInstallClick}
              size={{ base: 'sm', lg: 'md' }}
              bg="white"
              color="brand.600"
              fontWeight="700"
              leftIcon={<Icon as={FaDownload} />}
              _hover={{
                bg: 'gray.100',
                transform: 'scale(1.05)',
              }}
              _active={{
                bg: 'gray.200',
              }}
              transition="all 0.2s"
            >
              Install App
            </Button>
            <Button
              onClick={handleDontShowAgain}
              size={{ base: 'sm', lg: 'md' }}
              variant="ghost"
              color="white"
              fontWeight="600"
              _hover={{
                bg: 'whiteAlpha.200',
              }}
            >
              Don't Show Again
            </Button>
            <CloseButton
              onClick={handleDismiss}
              size="md"
              color="white"
              _hover={{
                bg: 'whiteAlpha.200',
              }}
            />
          </HStack>
        </Flex>
      </Box>

      {/* Mobile Prompt - Bottom Sheet */}
      <Box
        display={{ base: 'block', md: 'none' }}
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        zIndex={9999}
        bg="white"
        boxShadow="0 -4px 12px rgba(0, 0, 0, 0.15)"
        borderTopRadius="2xl"
        p={6}
      >
        <CloseButton
          position="absolute"
          top={4}
          right={4}
          onClick={handleDismiss}
          size="md"
        />

        <VStack spacing={4} align="stretch">
          <HStack spacing={4}>
            <Box
              bg="linear-gradient(135deg, #2196f3 0%, #00bcd4 100%)"
              p={3}
              borderRadius="xl"
            >
              <Icon as={FaMobileAlt} boxSize={8} color="white" />
            </Box>
            <VStack align="start" spacing={0} flex={1}>
              <Text fontSize="lg" fontWeight="700" color="gray.800">
                Install Our App
              </Text>
              <Text fontSize="sm" color="gray.600">
                Lake Lucien Surgery Center
              </Text>
            </VStack>
          </HStack>

          <VStack spacing={2} align="stretch">
            <Text fontSize="sm" color="gray.600">
              • Quick access from your home screen
            </Text>
            <Text fontSize="sm" color="gray.600">
              • Works offline
            </Text>
            <Text fontSize="sm" color="gray.600">
              • Faster loading times
            </Text>
          </VStack>

          <VStack spacing={2} align="stretch">
            <Button
              onClick={handleInstallClick}
              size="lg"
              bg="linear-gradient(135deg, #2196f3 0%, #00bcd4 100%)"
              color="white"
              fontWeight="700"
              leftIcon={<Icon as={FaDownload} />}
              _hover={{
                bg: 'linear-gradient(135deg, #1976d2 0%, #00acc1 100%)',
              }}
            >
              Install App
            </Button>
            <Button
              onClick={handleDontShowAgain}
              size="md"
              variant="ghost"
              color="gray.600"
              fontWeight="600"
            >
              Don't Show Again
            </Button>
          </VStack>
        </VStack>
      </Box>
    </>
  );
};

export default InstallPrompt;
